<script setup lang="ts">
/**
 * The consent screen (#326): a person chooses which company, which AI team member an outside app's connection works
 * as, and what it may do, then approves or declines.
 *
 * Asked in that order, and only where there is a real choice: one company is named rather than offered, because
 * picking from a list of one is a click that teaches nothing. Nothing is preselected further down — the member is
 * chosen, and every scope starts at `none`, exactly as API tokens do, because a consent screen that pre-ticks what it
 * is about to grant away is not consent.
 *
 * Said in plain words (#520): the app is named by the name it sent, or "an outside app", never by its internal client
 * id; each permission is described rather than given its resource id; the button pressed shows it is working until
 * the answer comes; a failure says what to do next rather than repeating the server; a code handed over on screen
 * comes with a Copy button and where to paste it; and the screen says where the connection can be removed later.
 *
 * The screen's answers are a request, not a decision: the server re-checks the membership, the member and the
 * scopes on approval, the membership again when the client redeems its code, and whether the `redirect_uri`
 * on the query string is one that client registered — the answer is only ever sent to a redirect that came back
 * from the server, never to the one this page was asked to use.
 */
import {
    Button,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Spinner,
    ZhCopyButton,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { type ConsentEnterpriseOption, type ConsentMemberOption, consentApi, refusalCode } from '@/api'
import ScopeLevelAll from '@/components/ScopeLevelAll.vue'
import ScopeLevelPicker from '@/components/ScopeLevelPicker.vue'
import { type ScopeLevel, useScopeLevels } from '@/composables/useScopeLevels'
import { grantAllowsScreen, grantWrites, type PortalResource, resourceForScreen } from '@/lib/navGrant'
import { RESOURCE_DESCRIPTIONS } from '@/lib/resourceDescriptions'
import { sessionEnterpriseId, sessionGrant } from '@/lib/sessionEnterprise'

/** Who is asking, and where they are sent back to. Read from the URL, as an OAuth authorization request. */
const query = new URLSearchParams(window.location.search)
const clientId = query.get('client_id') ?? ''
const clientName = query.get('client_name') ?? ''
const redirectUri = query.get('redirect_uri') ?? ''
const clientState = query.get('state') ?? ''
/**
 * What `/oauth/authorize` bound the request to (#325): the PKCE challenge and the MCP server it is for. Passed
 * on as they came; the server checks them again on approval rather than trusting this page.
 */
const oauthBinding: { codeChallenge?: string; codeChallengeMethod?: string; resource?: string } = {}
for (const [field, param] of [
    ['codeChallenge', 'code_challenge'],
    ['codeChallengeMethod', 'code_challenge_method'],
    ['resource', 'resource'],
] as const) {
    const value = query.get(param)
    if (value) {
        oauthBinding[field] = value
    }
}

/**
 * The app, as a person reads it: the name it sent, or "an outside app" when it sent none. Never the client id,
 * which is an internal handle that tells a person nothing. `appAtStart` starts a sentence; `app` sits inside one.
 */
const app = clientName || 'an outside app'
const appAtStart = clientName || 'An outside app'

const enterprises = ref<ConsentEnterpriseOption[]>([])
const members = ref<ConsentMemberOption[]>([])
const enterpriseId = ref('')
const memberId = ref('')
/** One level per base resource, the same shape API tokens are granted in, chosen with the same shared picker. */
const { levels, scopes } = useScopeLevels()
/** The picker's levels in this screen's words: a person granting access away reads a sentence, not a flag. */
const LEVEL_LABELS: Record<ScopeLevel, string> = {
    none: "Can't see it",
    read: 'Can see it',
    write: 'Can see and change it',
}

/** What a resource covers, in words: the picker shows it on the row and names the row's control after it. */
function describeResource(resource: string): string {
    return RESOURCE_DESCRIPTIONS[resource as PortalResource]
}

function scopeName(resource: string): string {
    return `What ${app} may do with ${describeResource(resource)}`
}

const loading = ref(true)
/** The chosen company's team members are on their way: "none yet" is only said once they have come back. */
const membersLoading = ref(false)
/** The chosen company's team members could not be loaded, so choosing it again is offered even when it is the only one. */
const membersFailed = ref(false)
/** Which answer is on its way, so the button pressed can say so. Null when neither is. */
const pending = ref<'approve' | 'decline' | null>(null)
const error = ref<string | null>(null)
/**
 * Whether the failure is an answer to Approve or Decline, so it is said by the buttons that were just pressed rather
 * than at the top of a long page, where the person may not see it.
 */
const errorByButtons = ref(false)
const buttonsError = ref<HTMLElement | null>(null)
/**
 * Shown when there is nowhere to send the person back to: the answer still has to be visible somewhere. An
 * approval carries the code the app needs, which the person passes on themselves.
 */
type Outcome = { kind: 'approved'; code: string } | { kind: 'declined' }
const outcome = ref<Outcome | null>(null)

/** One company is not a choice; several are. */
const onlyEnterprise = computed(() => (enterprises.value.length === 1 ? (enterprises.value[0] ?? null) : null))
/** The company chosen, whose Connected apps page is where this connection can be removed. */
const chosenEnterprise = computed(() => enterprises.value.find(enterprise => enterprise.id === enterpriseId.value))
const canApprove = computed(
    () => pending.value === null && enterpriseId.value !== '' && memberId.value !== '' && scopes.value.length > 0,
)

/** What is still missing before Approve can be pressed, said beside it rather than left to a greyed-out button. */
const approveHint = computed(() => {
    // With no team member on offer (still loading, failed, or none yet) the panel above already says why.
    if (!enterpriseId.value || outcome.value || pending.value !== null || members.value.length === 0) {
        return null
    }
    const noMember = memberId.value === ''
    const noScope = scopes.value.length === 0
    if (noMember && noScope) {
        return 'To approve, choose who it works as and at least one thing it may do.'
    }
    if (noMember) {
        return 'To approve, choose who it works as.'
    }
    return noScope ? 'To approve, choose at least one thing it may do.' : null
})

/**
 * How this person reaches Connected apps for the chosen company. That page lists the connections of the company the
 * session is signed into, and the router turns away a grant that does not open it (`grantAllowsScreen`, the same rule
 * the router applies), so the link is drawn only where it opens the right list: the session's own company, with a
 * grant that opens the page. Another company is reached by switching to it first; a grant without the page leaves it
 * to the company's owners. The linked line promises the person can *remove* the connection too, and removing one is a
 * write on the page's resource (the server holds every method but GET, HEAD and OPTIONS to `:write`), so a grant
 * that may only read the page is left to the owners as well (`grantWrites`, the same rule the portal's controls use).
 * A session or grant not known yet restricts nothing, as everywhere else in the portal's navigation. The shell sets
 * both before any signed-in screen renders, so reading them here is not a race.
 */
const CONNECTED_APPS = '/connected-apps'
const CONNECTED_APPS_RESOURCE = resourceForScreen(CONNECTED_APPS)
const undoRoute = computed<'link' | 'switch' | 'owner' | null>(() => {
    const chosen = chosenEnterprise.value
    if (!chosen) {
        return null
    }
    const signedInto = sessionEnterpriseId()
    if (signedInto !== null && signedInto !== chosen.id) {
        return 'switch'
    }
    const grant = sessionGrant()
    const opensPage = grantAllowsScreen(grant, CONNECTED_APPS)
    const removes = CONNECTED_APPS_RESOURCE === null || grantWrites(grant, CONNECTED_APPS_RESOURCE)
    return opensPage && removes ? 'link' : 'owner'
})
/** The undo line in words, where it carries no link. */
const undoSentence = computed(() => {
    const name = chosenEnterprise.value?.name ?? ''
    if (undoRoute.value === 'switch') {
        return `To see or remove this connection later, switch to ${name} and open Connected apps there.`
    }
    return `An owner of ${name} can see or remove this connection at any time in Connected apps.`
})

/** Choosing a company re-asks for its members: a member of the last one is not on offer here. */
async function chooseEnterprise(id: string): Promise<void> {
    enterpriseId.value = id
    memberId.value = ''
    members.value = []
    membersLoading.value = true
    membersFailed.value = false
    try {
        const found = await consentApi.members(id)
        // A company chosen since this one was asked for: its own answer is the one that counts.
        if (enterpriseId.value !== id) {
            return
        }
        members.value = found
        error.value = null
    } catch {
        if (enterpriseId.value !== id) {
            return
        }
        membersFailed.value = true
        errorByButtons.value = false
        error.value = "We couldn't load the team members for that company. Choose the company again to retry."
    } finally {
        if (enterpriseId.value === id) {
            membersLoading.value = false
        }
    }
}

onMounted(async () => {
    try {
        enterprises.value = await consentApi.enterprises()
        const only = onlyEnterprise.value
        if (only) {
            await chooseEnterprise(only.id)
        }
    } catch {
        errorByButtons.value = false
        error.value = "We couldn't load your companies. Refresh the page to try again."
    } finally {
        loading.value = false
    }
})

/** A failure answering Approve or Decline, said by the buttons and brought into view. */
async function sayByButtons(sentence: string): Promise<void> {
    errorByButtons.value = true
    error.value = sentence
    pending.value = null
    await nextTick()
    buttonsError.value?.scrollIntoView?.({ block: 'nearest' })
}

/**
 * Back to whoever asked, carrying the answer — but only to `confirmed`, the redirect the *server* said this
 * OS has registered, never to the `redirect_uri` read off this page's own query string. That string is
 * whatever the requester typed, and the approval carries a one-time code that is the whole grant: sending it
 * to an unregistered origin would hand the connection to whoever built the link. Returns whether the browser is
 * leaving; with nothing confirmed to return to, the caller shows the answer here instead.
 */
function sendBack(params: Record<string, string>, confirmed: string | undefined): boolean {
    let url: URL | null = null
    try {
        url = confirmed ? new URL(confirmed) : null
    } catch {
        url = null
    }
    if (!url) {
        return false
    }
    for (const [key, value] of Object.entries(params)) {
        url.searchParams.set(key, value)
    }
    if (clientState) {
        url.searchParams.set('state', clientState)
    }
    window.location.assign(url.toString())
    return true
}

/**
 * How long the page waits for the browser to leave for a confirmed return address before it shows the answer here
 * instead. Some return addresses open another program (a desktop app's own scheme) and leave this page where it is;
 * a button that says it is working for good would then be a dead end, with the code never shown.
 */
const STILL_HERE_MS = 5000
let stillHere: ReturnType<typeof setTimeout> | null = null
onBeforeUnmount(() => {
    if (stillHere) {
        clearTimeout(stillHere)
    }
})

function show(shown: Outcome): void {
    outcome.value = shown
    pending.value = null
}

/**
 * The answer is in. When the browser is leaving, the button pressed keeps saying it is working until the page goes:
 * buttons that reset just as the page leaves look like nothing happened. If the page is still here after a short
 * wait, the browser did not leave, and the answer is shown here as it is when there is nowhere to return to.
 */
function settle(leaving: boolean, shown: Outcome): void {
    if (!leaving) {
        show(shown)
        return
    }
    stillHere = setTimeout(() => {
        stillHere = null
        show(shown)
    }, STILL_HERE_MS)
}

async function approve(): Promise<void> {
    if (!canApprove.value) {
        return
    }
    pending.value = 'approve'
    error.value = null
    try {
        const approved = await consentApi.approve({
            enterpriseId: enterpriseId.value,
            memberId: memberId.value,
            scopes: scopes.value,
            clientId,
            clientName: clientName || null,
            redirectUri: redirectUri || null,
            ...oauthBinding,
        })
        settle(sendBack({ code: approved.code }, approved.redirectUri), { kind: 'approved', code: approved.code })
    } catch (caught) {
        // Asking for more than the person holds is refused the same way however often it is pressed: say what to change.
        if (refusalCode(caught) === 'consent_scope_exceeds_grant') {
            const company = chosenEnterprise.value?.name ?? 'this company'
            await sayByButtons(
                `The connection wasn't approved, because it asks for more than your own role in ${company} allows. Lower what it may do, then press Approve again.`,
            )
            return
        }
        await sayByButtons(
            `The connection wasn't approved. Press Approve to try again, or start connecting again from ${app}.`,
        )
    }
}

async function decline(): Promise<void> {
    pending.value = 'decline'
    error.value = null
    try {
        const declined = await consentApi.decline({ clientId, redirectUri: redirectUri || null })
        settle(sendBack({ error: declined.error }, declined.redirectUri), { kind: 'declined' })
    } catch {
        await sayByButtons("The refusal wasn't sent. Press Decline to try again.")
    }
}
</script>

<template>
	<main class="w-full space-y-6 p-4 md:p-8" data-testid="consent">
		<div class="space-y-1">
			<ZhPageTitle>Connect {{ app }}</ZhPageTitle>
			<p class="text-muted-foreground text-sm" data-testid="consent-client">
				{{ appAtStart }} wants to work inside one of your companies. Choose which company, who it works as, and what it may do.
			</p>
		</div>

		<p v-if="error && !errorByButtons" class="text-destructive text-sm" role="alert" data-testid="consent-error">{{ error }}</p>
		<div v-if="outcome" class="space-y-2 text-sm" role="status" data-testid="consent-outcome">
			<template v-if="outcome.kind === 'approved'">
				<p>Approved. {{ appAtStart }} needs this code to finish connecting.</p>
				<div class="flex flex-wrap items-center gap-3">
					<code class="font-mono">{{ outcome.code }}</code>
					<ZhCopyButton :value="outcome.code" />
				</div>
				<p>Paste this code into {{ app }} where it asks for a code.</p>
				<!-- Opened in a new tab here, so following it before pasting does not take the code off the screen. -->
				<p v-if="chosenEnterprise && undoRoute" class="text-muted-foreground" data-testid="consent-undo">
					<template v-if="undoRoute === 'link'">
						You can see or remove this connection at any time in
						<Button variant="link" as-child class="h-auto p-0 text-sm"><RouterLink :to="CONNECTED_APPS" target="_blank" rel="noopener">Connected apps</RouterLink></Button>
						for {{ chosenEnterprise.name }}.
					</template>
					<template v-else>{{ undoSentence }}</template>
				</p>
			</template>
			<p v-else>Declined. The request from {{ app }} was refused, and nothing was created.</p>
		</div>
		<Spinner v-if="loading" label="Loading…" class="text-sm" />

		<ZhPanel v-else class="space-y-3">
			<ZhSectionHeading>Which company</ZhSectionHeading>
			<p v-if="!enterprises.length" class="text-muted-foreground text-sm">
				You aren't a member of any company yet, so there is nothing to connect to.
			</p>
			<!--
				One company is named rather than offered, unless its team members failed to load: then it is offered
				like any other, so "Choose the company again to retry" has something to press.
			-->
			<p v-else-if="onlyEnterprise && !membersFailed" class="text-sm" data-testid="consent-enterprise-name">
				{{ onlyEnterprise.name }}
			</p>
			<!--
				Each option is the kit's interactive panel, so its hover and chosen hairline come from the kit. It is
				a toggle, so `aria-pressed` is both what a screen reader hears and what the panel draws as chosen.
			-->
			<div v-else class="grid gap-2 sm:grid-cols-2">
				<ZhPanel
					v-for="enterprise in enterprises"
					:key="enterprise.id"
					as="button"
					interactive
					type="button"
					class="flex flex-col gap-1 text-left"
					:aria-pressed="enterprise.id === enterpriseId"
					data-testid="consent-enterprise-option"
					@click="chooseEnterprise(enterprise.id)"
				>
					<span class="truncate font-medium">{{ enterprise.name }}</span>
					<span class="text-muted-foreground text-xs">{{ enterprise.slug }}</span>
				</ZhPanel>
			</div>
		</ZhPanel>

		<ZhPanel v-if="enterpriseId" class="space-y-3">
			<ZhSectionHeading>Who it works as</ZhSectionHeading>
			<Spinner v-if="membersLoading" label="Loading team members…" class="text-sm" />
			<!-- "None yet" only once the answer is in: a failed load is said as a failure, not as an empty company. -->
			<p v-else-if="!members.length && !membersFailed" class="text-muted-foreground text-sm">
				That company has no team members yet.
			</p>
			<!-- No member is chosen until one is: the empty choice is the placeholder, never an item. -->
			<div v-else-if="members.length" class="space-y-2">
				<Label for="consent-member-field">Team member</Label>
				<Select :model-value="memberId" data-testid="consent-member" @update:model-value="memberId = $event as string">
					<SelectTrigger id="consent-member-field" class="w-full">
						<SelectValue placeholder="Choose a member…" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem v-for="member in members" :key="member.id" :value="member.id">{{ member.name }}</SelectItem>
					</SelectContent>
				</Select>
			</div>
		</ZhPanel>

		<ZhPanel v-if="enterpriseId" class="space-y-3">
			<div class="flex flex-wrap items-center justify-between gap-3">
				<ZhSectionHeading>What it may do</ZhSectionHeading>
				<ScopeLevelAll
					v-model="levels"
					testid="consent-scope-all"
					:level-labels="LEVEL_LABELS"
					credential
					connection
				/>
			</div>
			<p class="text-muted-foreground text-sm">Nothing is granted until you choose it.</p>
			<ScopeLevelPicker
				v-model="levels"
				testid-prefix="consent-scope-"
				:level-labels="LEVEL_LABELS"
				:name-for="scopeName"
				:label-for="describeResource"
				credential
				connection
			/>
			<!-- #883: the one resource here that is yours rather than the company's, said before it is granted. -->
			<p class="text-muted-foreground text-sm" data-testid="consent-mail-note">
				Your own mailbox is yours, not the company's. <strong>Can see it</strong> lets {{ app }} list and read
				your mail as you, and nobody else's. <strong>Can see and change it</strong> also lets it reply and send
				from your address, as you, with no approval step. You can give it whatever your role in this company, and
				no API token can ever hold it.
			</p>
		</ZhPanel>

		<div class="space-y-3">
			<div class="flex items-center gap-3">
				<Button
					type="button"
					:disabled="!canApprove"
					:aria-busy="pending === 'approve'"
					data-testid="consent-approve"
					@click="approve"
				>
					<Spinner v-if="pending === 'approve'" label="Approving…" class="text-current" />
					<template v-else>Approve</template>
				</Button>
				<Button
					type="button"
					variant="outline"
					:disabled="pending !== null"
					:aria-busy="pending === 'decline'"
					data-testid="consent-decline"
					@click="decline"
				>
					<Spinner v-if="pending === 'decline'" label="Declining…" class="text-current" />
					<template v-else>Decline</template>
				</Button>
			</div>
			<!-- A failure answering a button is said by the buttons, where the person just pressed. -->
			<p
				v-if="error && errorByButtons"
				ref="buttonsError"
				class="text-destructive text-sm"
				role="alert"
				data-testid="consent-error"
			>
				{{ error }}
			</p>
			<p v-if="approveHint" class="text-muted-foreground text-sm" data-testid="consent-approve-hint">{{ approveHint }}</p>
			<!--
				Said before approving as well as after: with a confirmed return address the page leaves the moment the
				approval lands, so a line shown only afterwards would never be read. Connected apps belongs to one
				company, so the line waits until one is chosen. It opens in a new tab, so following it does not lose
				this request.
			-->
			<p v-if="chosenEnterprise && undoRoute && !outcome" class="text-muted-foreground text-sm" data-testid="consent-undo">
				<template v-if="undoRoute === 'link'">
					You can see or remove this connection at any time in
					<Button variant="link" as-child class="h-auto p-0 text-sm"><RouterLink :to="CONNECTED_APPS" target="_blank" rel="noopener">Connected apps</RouterLink></Button>
					for {{ chosenEnterprise.name }}.
				</template>
				<template v-else>{{ undoSentence }}</template>
			</p>
		</div>
	</main>
</template>
